import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Users, Sparkles, Clock } from "lucide-react";
import { devWarn } from "@/lib/uiStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30000;
const CLOCK_TICK_MS = 15000;

export function timeAgo(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffSec = Math.max(0, Math.floor((now - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function formatCount(n) {
  if (typeof n !== "number") return "—";
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.floor(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

const plural = (n, word) => `${n === 1 ? word : `${word}s`}`;

/** Polls /stats/live and re-renders every 15s so "X ago" stays fresh. */
function useLiveStats() {
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats/live`, { timeout: 5000 });
        if (alive) setStats(res.data);
      } catch (err) {
        // Landing page must still render if the DB is slow — ticker just stays hidden.
        if (alive) devWarn("LiveProofTicker: stats fetch failed", err?.message);
      }
    };
    fetchStats();
    const fetchId = setInterval(fetchStats, POLL_INTERVAL_MS);
    const clockId = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => { alive = false; clearInterval(fetchId); clearInterval(clockId); };
  }, []);

  return { stats, now };
}

function buildItems(stats, now) {
  const lastGen = timeAgo(stats.latest_generation_at, now);
  return [
    stats.active_creators_last_24h > 0 && {
      key: "active", Icon: Activity, color: "text-violet-400",
      label: `${stats.active_creators_last_24h} ${plural(stats.active_creators_last_24h, "creator")} active today`,
    },
    lastGen && { key: "latest", Icon: Clock, color: "text-purple-300", label: `Last creative generated ${lastGen}` },
    stats.total_generations > 0 && {
      key: "total-gen", Icon: Sparkles, color: "text-cyan-300",
      label: `${formatCount(stats.total_generations)} ${plural(stats.total_generations, "asset")} generated`,
    },
    stats.total_creators > 0 && {
      key: "total-creators", Icon: Users, color: "text-pink-300",
      label: `${formatCount(stats.total_creators)} ${plural(stats.total_creators, "creator")} on Lumora`,
    },
  ].filter(Boolean);
}

const LiveDot = () => (
  <span className="flex items-center gap-1.5 text-[11px] text-violet-400 font-bold uppercase tracking-wider">
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-60 animate-ping" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-400" />
    </span>
    Live
  </span>
);

const TickerItem = ({ item: { key, Icon, color, label } }) => (
  <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.3 }} data-testid={`live-proof-${key}`}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] backdrop-blur-md text-white/80 text-xs font-medium">
    <Icon className={`w-3.5 h-3.5 ${color}`} />
    <span>{label}</span>
  </motion.div>
);

export const LiveProofTicker = () => {
  const { stats, now } = useLiveStats();
  // No real data yet — don't fake it. Hide the ticker entirely.
  if (!stats || (stats.total_generations === 0 && stats.total_creators === 0)) return null;
  const items = buildItems(stats, now);
  if (items.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.9 }}
      className="mt-6 flex flex-wrap items-center gap-3" data-testid="live-proof-ticker" aria-label="Live platform activity">
      <LiveDot />
      <AnimatePresence mode="popLayout">
        {items.map((it) => <TickerItem key={it.key} item={it} />)}
      </AnimatePresence>
    </motion.div>
  );
};
